import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMemo } from "react";

interface AvatarProps {
  name: string;
  imageUrl?: string;
}

export function getAvatarInitials(name: string): string {
  if (!name) return "";

  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9]/g, ""))
    .filter(Boolean);

  if (words.length > 1) {
    return words[0][0].toUpperCase() + words[1][0].toUpperCase();
  }

  const word = words[0];
  return word.substring(0, 2).toUpperCase();
}

const avatarBgColor = [
  "#DDEBFE",
  "#CCF4E3",
  "#FFEED8",
  "#F9DADE",
  "#E4DBF6",
  "#F2E6E2",
];

function pickColorForName(name: string) {
  if (!name) return avatarBgColor[0];
  const sum = Array.from(name).reduce(
    (acc, ch) => acc + ch.charCodeAt(0),
    0
  );
  return avatarBgColor[sum % avatarBgColor.length];
}

export function NameAvatar({ name = "", imageUrl }: AvatarProps) {
  const initials = getAvatarInitials(name);
  const bgColor = useMemo(() => pickColorForName(name), [name]);

  return (
    <Avatar className="h-6 w-6">
      <AvatarImage
        src={imageUrl || undefined}
        alt={initials}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.src = "";
        }}
      />
      <AvatarFallback
        style={{ backgroundColor: bgColor }}
        className="text-[10px] font-normal text-[var(--secondary-text-color)]"
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
